import { useState } from 'react';
import { Stack, Paper, Group, Text, Badge, Button, ActionIcon, Modal, CopyButton, Loader, Center } from '@mantine/core';
import { IconCopy, IconEye, IconCheck } from '@tabler/icons-react';
import MilkdownRenderer from '../shared/MilkdownRenderer';
import { ResumeVersion, ResumeContent } from '../../api/resumes';

interface ResumeVersionsListProps {
  versions: ResumeVersion[];
  contentItems: ResumeContent[];
}

export default function ResumeVersionsList({ versions, contentItems }: ResumeVersionsListProps) {
  if (versions.length === 0) {
    return (
      <Center py="xl">
        <Stack align="center" gap="sm">
          <Text c="dimmed">No resume versions generated yet</Text>
          <Text size="sm" c="dimmed">
            Use the Resume Compiler to create your first version
          </Text>
        </Stack>
      </Center>
    );
  }

  return (
    <Stack gap="md">
      {versions.map((version) => (
        <VersionCard key={version.id} version={version} contentItems={contentItems} />
      ))}
    </Stack>
  );
}

interface VersionCardProps {
  version: ResumeVersion;
  contentItems: ResumeContent[];
}

function VersionCard({ version, contentItems }: VersionCardProps) {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  // Get names of used content (with fallback for old data)
  const usedContentNames = (version.used_content_ids || [])
    .map((id) => contentItems.find((item) => item.id === id)?.name)
    .filter(Boolean);

  const handleCopyMarkdown = () => {
    navigator.clipboard.writeText(version.content_markdown);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <>
      <Paper p="md" radius="md" withBorder>
        <Group justify="space-between" mb="xs">
          <div>
            <Text fw={600}>{version.name}</Text>
            <Text size="xs" c="dimmed">
              Created {new Date(version.created_at).toLocaleDateString()}
            </Text>
          </div>
          <Group gap="xs">
            <CopyButton value={version.content_markdown} timeout={2000}>
              {({ copied: isCopied, copy }) => (
                <Button
                  size="xs"
                  variant="light"
                  onClick={copy}
                  leftSection={isCopied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                >
                  {isCopied ? 'Copied' : 'Copy'}
                </Button>
              )}
            </CopyButton>
            <Button
              size="xs"
              variant="default"
              leftSection={<IconEye size={14} />}
              onClick={() => setPreviewOpen(true)}
            >
              Preview
            </Button>
          </Group>
        </Group>

        {/* Used Content */}
        <Group gap="xs" mb="md">
          {usedContentNames.map((name, i) => (
            <Badge key={i} size="sm" variant="light">
              {name}
            </Badge>
          ))}
        </Group>

        {/* Preview text */}
        <Paper p="sm" bg="gray.0" radius="sm">
          <Text size="xs" c="dimmed" lineClamp={3}>
            {version.content_markdown}
          </Text>
        </Paper>
      </Paper>

      {/* Preview Modal */}
      <Modal
        opened={previewOpen}
        onClose={() => setPreviewOpen(false)}
        title={`Preview: ${version.name}`}
        size="xl"
      >
        <Paper p="md" radius="md" bg="gray.0" withBorder>
          <MilkdownRenderer content={version.content_markdown} />
        </Paper>
      </Modal>
    </>
  );
}
